import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ArrowRight, Clock, Hash, BarChart } from "lucide-react";

export default function Catalog() {
  const { t, tField, lang } = useI18n();
  const [search] = useSearchParams();
  const initialCat = search.get("cat") || "all";
  const [data, setData] = useState({ categories: [], items: [] });
  const [filter, setFilter] = useState(initialCat);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/diagnostics", { params: { lang } })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const filtered = useMemo(() => {
    if (filter === "all") return data.items;
    return data.items.filter((i) => i.category === filter);
  }, [data, filter]);

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 lg:py-16">
      <div className="mb-10">
        <div className="label-mono mb-2">// Catalog</div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight uppercase text-white">
          {t("catalog.title")}
        </h1>
        <p className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-[#94A3B8]">{t("catalog.subtitle")}</p>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-10">
        <button
          data-testid="catalog-filter-all"
          onClick={() => setFilter("all")}
          className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] font-bold border ${
            filter === "all"
              ? "bg-[#3B82F6] text-black border-[#3B82F6]"
              : "bg-[#0F1525] text-white border-[#1E2A3F] hover:border-[#3B82F6]"
          }`}
        >
          {t("catalog.filter_all")}
        </button>
        {data.categories.map((c) => (
          <button
            key={c.id}
            data-testid={`catalog-filter-${c.id}`}
            onClick={() => setFilter(c.id)}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] font-bold border ${
              filter === c.id
                ? "bg-[#3B82F6] text-black border-[#3B82F6]"
                : "bg-[#0F1525] text-white border-[#1E2A3F] hover:border-[#3B82F6]"
            }`}
          >
            {tField(c.label)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#64748B] py-20 text-center font-mono uppercase text-xs tracking-[0.2em]">
          // Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item, idx) => (
            <Link
              key={item.id}
              to={`/quiz/${item.id}`}
              data-testid={`catalog-card-${item.id}`}
              className="group bg-[#0F1525] border border-[#1E2A3F] p-6 card-hoverable slide-up"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="px-2 py-1 bg-[#3B82F6] text-black font-mono text-[10px] uppercase tracking-[0.2em] font-bold">
                  {tField(item.category_label)}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                  #{String(idx + 1).padStart(2, "0")}
                </div>
              </div>
              <h3 className="font-display text-2xl tracking-tight uppercase text-white mb-2">
                {tField(item.title)}
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed min-h-[40px]">
                {tField(item.subtitle)}
              </p>
              <div className="mt-6 pt-6 border-t border-[#1E2A3F] flex items-center justify-between font-mono text-[11px] text-[#94A3B8]">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3 text-[#3B82F6]" strokeWidth={1.5} />
                    {item.duration_min} {t("catalog.duration")}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Hash className="h-3 w-3 text-[#3B82F6]" strokeWidth={1.5} />
                    {item.question_count} {t("catalog.question_count")}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <BarChart className="h-3 w-3 text-[#3B82F6]" strokeWidth={1.5} />
                    {t(`catalog.difficulty.${item.difficulty}`)}
                  </span>
                </div>
                <ArrowRight className="h-4 w-4 text-[#3B82F6] transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
